import React, { useState, useEffect, useRef } from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { useCreditConfig } from '../../../contexts/CreditConfigContext';
import { generateDownloadFilename } from '../../../utils/fileUtils';
import soraTextVideo from '../../../assets/video/sora_text_gererate_video.mp4';
import soraImageVideo from '../../../assets/video/sora_image_generate_video.mp4';
import './Sora2Generator.scss';

export default function Sora2Generator() {
  const { t } = useI18n();
  const { creditConfig } = useCreditConfig();
  const [mode, setMode] = useState('text');
  const [prompt, setPrompt] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const inputRef = useRef(null);
  const [model, setModel] = useState('sora2');
  const [ratio, setRatio] = useState('landscape');
  const [duration, setDuration] = useState('10s');
  const [rmWatermark, setRmWatermark] = useState(false);
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const canGenerate = mode === 'text' ? prompt.trim().length > 0 : !!imageFile;

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return;
    setImageFile(f);
    const url = URL.createObjectURL(f);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
  };

  const onReplace = (e) => {
    e.stopPropagation();
    inputRef.current && inputRef.current.click();
  };
  const onRemove = (e) => {
    e.stopPropagation();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setImageFile(null);
  };
  const openPreview = (e) => {
    e.stopPropagation();
    setShowPreview(true);
  };
  const closePreview = () => setShowPreview(false);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleGenerate = () => {
    if (!canGenerate) return;
    setIsGenerating(true);
    setResult(null);
    
    // Simulate generation
    setTimeout(() => {
      setResult({
        url: mode === 'text' ? soraTextVideo : soraImageVideo,
        id: Math.random().toString(36).substring(7)
      });
      setIsGenerating(false);
    }, 3000);
  };

  const handleDownload = async () => {
    if (!result?.url) return;
    try {
      const response = await fetch(result.url);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      const filename = generateDownloadFilename(result.id);
      a.download = `${filename}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error('Download failed:', e);
      const a = document.createElement('a');
      a.href = result.url;
      const filename = generateDownloadFilename(result.id);
      a.download = `${filename}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const vg = creditConfig?.videoGeneration;
  const credits = vg?.costStandard;

  return (
    <section className="sora2-generator">
      <div className="sora2-generator__grid">
        <div className="sora2-generator__left">
          <div className="sora2-tabs">
            <button className={mode === 'text' ? 'sora2-tab active' : 'sora2-tab'} onClick={() => setMode('text')}>{t('videoGeneration.sora.tabs.textToVideo')}</button>
            <button className={mode === 'image' ? 'sora2-tab active' : 'sora2-tab'} onClick={() => setMode('image')}>{t('videoGeneration.sora.tabs.imageToVideo')}</button>
          </div>

          {mode === 'image' && (
            <div className={imageFile ? 'sora2-drop has-file' : 'sora2-drop'}>
              <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFileChange} style={imageFile ? { display: 'none' } : undefined} />
              {imageFile ? (
                <div className="sora2-drop__preview" onClick={openPreview}>
                  {previewUrl && <img src={previewUrl} alt="preview" className="sora2-drop__img" />}
                  <div className="veo2-upload__actions">
                    <button className="veo2-action is-ok" onClick={onReplace}>↻</button>
                    <button className="veo2-action is-danger" onClick={onRemove}>×</button>
                  </div>
                </div>
              ) : (
                <div className="sora2-drop__hint">
                  <div className="sora2-drop__icon">⬆︎</div>
                  <div className="sora2-drop__text">{t('videoGeneration.sora.upload.uploadImage')}<br/>{t('videoGeneration.sora.upload.formats')}</div>
                </div>
              )}
            </div>
          )}

          <div className="sora2-field">
            <div className="sora2-field__label">{t('videoGeneration.sora.prompt.label')}</div>
            <div className="sora2-textarea">
              <textarea
                placeholder={t('videoGeneration.sora.prompt.placeholder')}
                maxLength={5000}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
              <div className="sora2-textarea__sub">
                <span>{t('videoGeneration.sora.prompt.sub')}</span>
                <span>{t('videoGeneration.sora.prompt.count', { count: prompt.length })}</span>
              </div>
            </div>
          </div>

          <div className="sora2-group">
            <div className="sora2-group__title">{t('videoGeneration.sora.model.title')}</div>
            <div className="sora2-options">
              <button className={model === 'sora2' ? 'sora2-opt active green' : 'sora2-opt green'} onClick={() => setModel('sora2')}>{t('videoGeneration.sora.model.sora2')}</button>
              <button className={model === 'sora2pro' ? 'sora2-opt active green' : 'sora2-opt green'} onClick={() => setModel('sora2pro')}>{t('videoGeneration.sora.model.sora2pro')}</button>
            </div>
          </div>

          <div className="sora2-group">
            <div className="sora2-group__title">{t('videoGeneration.sora.groups.aspect')}</div>
            <div className="sora2-options">
              <button className={ratio === 'landscape' ? 'sora2-opt active green' : 'sora2-opt green'} onClick={() => setRatio('landscape')}>{t('videoGeneration.sora.options.landscape')}</button>
              <button className={ratio === 'portrait' ? 'sora2-opt active green' : 'sora2-opt green'} onClick={() => setRatio('portrait')}>{t('videoGeneration.sora.options.portrait')}</button>
            </div>
          </div>

          <div className="sora2-group">
            <div className="sora2-group__title">{t('videoGeneration.sora.groups.duration')}</div>
            <div className="sora2-options">
              <button className={duration === '10s' ? 'sora2-opt active green' : 'sora2-opt green'} onClick={() => setDuration('10s')}>{t('videoGeneration.sora.options.s10')}</button>
              <button className={duration === '15s' ? 'sora2-opt active green' : 'sora2-opt green'} onClick={() => setDuration('15s')}>{t('videoGeneration.sora.options.s15')}</button>
            </div>
          </div>

          <div className="sora2-toggle">
            <span>{t('videoGeneration.sora.toggle.removeWatermark')}</span>
            <button className={rmWatermark ? 'switch on' : 'switch'} onClick={() => setRmWatermark(!rmWatermark)}>
              <span className="knob" />
            </button>
          </div>

          {typeof credits === 'number' && (
            <div className="sora2-credits">
              <div className="sora2-credits__bar">
                <span>{t('videoGeneration.sora.credits.bar')}</span>
                <span className="sora2-credits__value">{t('videoGeneration.sora.credits.value', { value: credits })}</span>
              </div>
            </div>
          )}

          <button 
            className={`sora2-generate ${canGenerate ? 'active' : ''} ${isGenerating ? 'generating' : ''}`} 
            disabled={!canGenerate || isGenerating}
            onClick={handleGenerate}
          >
            {isGenerating ? t('aiToolTemplate.generating') : (typeof credits === 'number' ? t('videoGeneration.sora.actions.generate', { value: credits }) : t('aiworkbench.generation.startGeneration'))}
          </button>
        </div>

        <div className="sora2-generator__right">
          <div className={`sora2-preview ${result ? 'has-result' : ''}`}>
            {result ? (
              <div className="veo2-video">
                <video className="veo2-video__player" src={result.url} controls autoPlay loop muted playsInline />
                <div className="veo2-video__bar">
                  <div className="veo2-video__info">
                    <div className="veo2-video__label">Generated Video</div>
                    <div className="veo2-video__meta">10s • 720P</div>
                  </div>
                  <button className="veo2-video__download" onClick={handleDownload}>
                    Download
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="sora2-preview__icon">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="5" width="18" height="14" rx="2" stroke="#16a34a" strokeWidth="1.5"/>
                    <circle cx="8" cy="12" r="2" stroke="#16a34a" strokeWidth="1.5"/>
                    <path d="M12 16l3-4 4 6" stroke="#16a34a" strokeWidth="1.5"/>
                  </svg>
                </div>
                <div className="sora2-preview__title">{t('videoGeneration.sora.preview.title')}</div>
                <div className="sora2-preview__desc">{t('videoGeneration.sora.preview.desc')}</div>
              </>
            )}
            {isGenerating && (
              <div className="veo2-preview__mask">
                <div className="veo2-loading">
                  <div className="veo2-spinner" />
                  <span>{t('aiToolTemplate.generating')}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {showPreview && previewUrl && (
          <div className="veo2-modal" onClick={closePreview}>
            <div className="veo2-modal__content" onClick={(e) => e.stopPropagation()}>
              <img src={previewUrl} alt="preview" className="veo2-modal__img" />
              <button className="veo2-modal__close" onClick={closePreview}>×</button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
